import { useCallback } from 'react';
import {
  markCleanupHandled,
  isCleanupHandled,
} from '../utils/cleanupHandledError';

/**
 * Wraps an `onComplete` callback so that if it throws — or, since
 * `onComplete` may be async, its returned promise rejects — a cleanup
 * routine (e.g. deleting the secret/account that was just created) runs
 * before the error is rethrown. `shouldDeleteOnFailure` lets the consumer
 * opt out of cleanup for a given id; when omitted, cleanup always runs.
 *
 * Awaiting `onComplete` here (rather than firing it and moving on) matters
 * beyond this hook: callers await the function this returns before treating
 * the flow as done, so a rejecting `onComplete` correctly fails the attempt
 * instead of it silently succeeding.
 *
 * `onError`, if provided, runs before cleanup — useful for telemetry that
 * needs the caught error (e.g. `captureException`).
 *
 * If the error is already marked via `markCleanupHandled` (e.g. a nested
 * `onComplete` already cleaned up and reported it — see buildUnifiedAccount's
 * handleComplete), this skips onError/cleanup to avoid doing either twice,
 * but still rethrows so the caller sees the failure.
 *
 * Shared by the account and auth modals, which otherwise each reimplement
 * the same await/cleanup/rethrow skeleton around `onComplete`.
 */
export function useCompleteWithCleanup<TId>(
  onComplete: (id: TId) => void | Promise<void>,
  shouldDeleteOnFailure: ((id: TId) => boolean) | undefined,
  cleanup: (id: TId) => void,
  onError?: (error: unknown, id: TId) => void
): (id: TId) => Promise<void> {
  return useCallback(
    async (id: TId) => {
      try {
        await onComplete(id);
      } catch (error) {
        if (!isCleanupHandled(error)) {
          try {
            onError?.(error, id);
          } catch {
            // Suppress errors from onError so cleanup always runs and the
            // original error is rethrown unchanged.
          }
          const shouldCleanup = shouldDeleteOnFailure
            ? shouldDeleteOnFailure(id)
            : true;
          if (shouldCleanup) {
            cleanup(id);
          }
          markCleanupHandled(error);
        }
        throw error;
      }
    },
    [onComplete, shouldDeleteOnFailure, cleanup, onError]
  );
}
